import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import PageTransition from '../components/PageTransition';
import { Order, orderService } from '../services/orderService';

const Orders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const theme = useTheme();

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await orderService.getMyOrders();
      setOrders(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des commandes');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    try {
      await orderService.cancelOrder(selectedOrder.id);
      setSnackbar({
        open: true,
        message: 'Commande annulée avec succès',
      });
      fetchOrders();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Erreur lors de l'annulation de la commande",
      });
    } finally {
      setOpenCancelDialog(false);
      setSelectedOrder(null);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'delivered':
        return theme.palette.success.main;
      case 'shipped':
      case 'processing':
      case 'pending':
        return theme.palette.warning.main;
      case 'cancelled':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'delivered':
        return 'Livrée';
      case 'shipped':
        return 'Expédiée';
      case 'processing':
        return 'En traitement';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageTransition>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Chargement des commandes...
            </Typography>
          </Box>
        </Container>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <PageTransition>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="h6" color="error">
              {error}
            </Typography>
            <Button variant="contained" color="primary" onClick={fetchOrders} sx={{ mt: 2 }}>
              Réessayer
            </Button>
          </Paper>
        </Container>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Mes commandes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Consultez l'historique de vos commandes et leur statut
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {orders.map((order) => (
            <Card
              key={order.id}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Commande #{order.id}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getStatusColor(order.status),
                        fontWeight: 'medium',
                      }}
                    >
                      {getStatusText(order.status)}
                    </Typography>
                    {order.status === 'pending' && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedOrder(order);
                          setOpenCancelDialog(true);
                        }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatDate(order.created_at)}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  {order.OrderItems.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {item.quantity}x {item.Product.name}
                      </Typography>
                      <Typography variant="body2">
                        {(item.price * item.quantity).toFixed(2)} €
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {Number(order.totalAmount).toFixed(2)} €
                  </Typography>
                </Box>

                {order.shippingAddress && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Adresse de livraison :
                      </Typography>
                      {(() => {
                        try {
                          const address = JSON.parse(order.shippingAddress);
                          return (
                            <Box sx={{ pl: 1 }}>
                              <Typography variant="body2">
                                {address.firstName} {address.lastName}
                              </Typography>
                              <Typography variant="body2">{address.address}</Typography>
                              {address.address2 && (
                                <Typography variant="body2">{address.address2}</Typography>
                              )}
                              <Typography variant="body2">
                                {address.postalCode} {address.city}
                              </Typography>
                              <Typography variant="body2">{address.country}</Typography>
                            </Box>
                          );
                        } catch (e) {
                          return (
                            <Typography variant="body2" color="error">
                              Format d'adresse invalide
                            </Typography>
                          );
                        }
                      })()}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ))}

          {orders.length === 0 && (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Vous n'avez pas encore de commandes
              </Typography>
            </Paper>
          )}
        </Box>

        <Dialog
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: '400px',
            },
          }}
        >
          <DialogTitle>Annuler la commande</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir annuler cette commande ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setOpenCancelDialog(false)} sx={{ color: 'text.secondary' }}>
              Retour
            </Button>
            <Button onClick={handleCancelOrder} variant="contained" color="error" autoFocus>
              Annuler la commande
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    </PageTransition>
  );
};

export default Orders;
